import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Spinner,
  Banner,
  Box,
  Divider,
} from '@shopify/polaris';
import {
  PackageIcon,
  DeliveryIcon,
  AlertCircleIcon,
} from '@shopify/polaris-icons';
import { api } from '../utils/api';
import StatsCard from '../components/StatsCard';
import RecentShipments from '../components/RecentShipments';
import StatusChart from '../components/StatusChart';

interface DashboardStats {
  total: number;
  pending: number;
  inTransit: number;
  outForDelivery: number;
  delivered: number;
  exceptions: number;
  deliveryRate: number;
  averageDeliveryTime: number;
}

export default function Dashboard() {
  const { data: stats, isLoading, error } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: () => api.get('/shipments/stats'),
  });

  if (isLoading) {
    return (
      <Page title="Tableau de bord">
        <Box padding="800">
          <InlineStack align="center">
            <Spinner size="large" />
          </InlineStack>
        </Box>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Tableau de bord">
        <Banner tone="critical">
          Erreur lors du chargement des statistiques
        </Banner>
      </Page>
    );
  }

  const chartData = stats ? [
    { name: 'En attente', value: stats.pending, color: '#6B7280' },
    { name: 'En transit', value: stats.inTransit, color: '#8B5CF6' },
    { name: 'En livraison', value: stats.outForDelivery, color: '#F59E0B' },
    { name: 'Livrés', value: stats.delivered, color: '#10B981' },
    { name: 'Exceptions', value: stats.exceptions, color: '#EF4444' },
  ] : [];

  return (
    <Page title="Tableau de bord">
      <BlockStack gap="500">
        {/* Stats Cards */}
        <Layout>
          <Layout.Section variant="oneThird">
            <StatsCard
              title="Total expéditions"
              value={stats?.total || 0}
              icon={PackageIcon}
              helpText="Ce mois"
            />
          </Layout.Section>
          <Layout.Section variant="oneThird">
            <StatsCard
              title="En transit"
              value={stats?.inTransit || 0}
              icon={DeliveryIcon}
              trend="neutral"
            />
          </Layout.Section>
          <Layout.Section variant="oneThird">
            <StatsCard
              title="Livrés"
              value={stats?.delivered || 0}
              icon={DeliveryIcon}
              trend="positive"
              trendValue={`${stats?.deliveryRate || 0}%`}
            />
          </Layout.Section>
        </Layout>
        <Layout>
          <Layout.Section variant="oneThird">
            <StatsCard
              title="Exceptions"
              value={stats?.exceptions || 0}
              icon={AlertCircleIcon}
              trend={stats?.exceptions && stats.exceptions > 0 ? 'negative' : 'neutral'}
            />
          </Layout.Section>
        </Layout>

        {/* Main Content */}
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Expéditions récentes
                </Text>
                <RecentShipments />
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section variant="oneThird">
            <BlockStack gap="400">
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Répartition par statut
                  </Text>
                  <StatusChart data={chartData} />
                </BlockStack>
              </Card>

              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Performances
                  </Text>
                  <Divider />
                  <InlineStack align="space-between">
                    <Text as="span" variant="bodyMd">
                      Taux de livraison
                    </Text>
                    <Badge tone="success">{`${stats?.deliveryRate || 0}%`}</Badge>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" variant="bodyMd">
                      Délai moyen
                    </Text>
                    <Badge>{`${stats?.averageDeliveryTime || 0} jours`}</Badge>
                  </InlineStack>
                </BlockStack>
              </Card>
            </BlockStack>
          </Layout.Section>
        </Layout>
      </BlockStack>
    </Page>
  );
}
